import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { deserializeFontFamilies } from '@data/deserialization/font-families';
import {
    COMPLETE_CREATIVESET_SHOWCASE_TRANSLATION,
    CREATE_CREATIVESET_SHOWCASE,
    DELETE_CREATIVESET_SHOWCASE,
    GET_SHOWCASES_OF_CREATIVESET,
    UPDATE_CREATIVESET_SHOWCASE
} from '@data/graphql/showcase.queries';
import { CreativeSetShowcaseDtoV2 } from '@domain/api/generated/sapi';
import { EnvironmentService } from '@studio/common/services/environment.service';
import {
    ICreativesetShowcase,
    ICreativesetShowcaseCreateData,
    ICreativesetShowcasesResponse,
    IShowcaseCreativesetResponse,
    IShowcaseCreativesetUpdateResponse
} from '@studio/domain/showcase';
import { Apollo } from 'apollo-angular';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { convertBrandDto } from '../creativeset-showcase.utils';

@Injectable({
    providedIn: 'root'
})
export class CreativeSetShowcaseDataService {
    private apollo = inject(Apollo);
    private environmentService = inject(EnvironmentService);
    private httpClient = inject(HttpClient);

    getShowcasesOfCreativeSet(creativesetId: string): Observable<ICreativesetShowcasesResponse> {
        return this.apollo
            .watchQuery<{ showcasesByCreativesetId: ICreativesetShowcasesResponse }>({
                query: GET_SHOWCASES_OF_CREATIVESET,
                variables: {
                    creativesetId: creativesetId
                }
            })
            .valueChanges.pipe(
                map(({ data }) => ({
                    ...data.showcasesByCreativesetId,
                    __typename: undefined,
                    showcases: data.showcasesByCreativesetId.showcases.map(s => ({
                        ...s,
                        __typename: undefined
                    }))
                }))
            );
    }

    createCreativeSetShowcase(
        showcase: ICreativesetShowcaseCreateData
    ): Observable<ICreativesetShowcase> {
        return this.apollo
            .mutate<{ createCreativesetShowcase: ICreativesetShowcase }>({
                mutation: CREATE_CREATIVESET_SHOWCASE,
                variables: {
                    creativesetShowcase: showcase
                }
            })
            .pipe(
                map(({ data }) => ({
                    ...data!.createCreativesetShowcase,
                    __typename: undefined
                }))
            );
    }

    updateCreativeSetShowcase(showcase: ICreativesetShowcase): Observable<ICreativesetShowcase> {
        return this.apollo
            .mutate<{ updateCreativesetShowcase: ICreativesetShowcase }>({
                mutation: UPDATE_CREATIVESET_SHOWCASE,
                variables: {
                    creativesetShowcase: showcase
                }
            })
            .pipe(
                map(({ data }) => ({
                    ...data!.updateCreativesetShowcase,
                    __typename: undefined
                }))
            );
    }

    deleteCreativeSetShowcase(showcaseKey: string): Observable<boolean> {
        return this.apollo
            .mutate<{ deleteCreativesetShowcase: boolean }>({
                mutation: DELETE_CREATIVESET_SHOWCASE,
                variables: {
                    showcaseKey
                }
            })
            .pipe(map(({ data }) => data!.deleteCreativesetShowcase));
    }

    getShowcaseCreativeSet(key: string): Observable<IShowcaseCreativesetResponse> {
        const { sapi } = this.environmentService.origins;
        const url = `${sapi}/showcase/${key}`;
        return this.httpClient
            .get<CreativeSetShowcaseDtoV2>(url)
            .pipe(map(data => this.mapToShowcaseCreativeSetResponse(data)));
    }

    private mapToShowcaseCreativeSetResponse(
        response: CreativeSetShowcaseDtoV2
    ): IShowcaseCreativesetResponse {
        const { allowedOperations, creativeSet, brand, showcaseToken, fonts } = response;

        return {
            allowedOperations,
            creativeset: creativeSet,
            showcaseToken,
            brand: convertBrandDto(brand),
            fonts: deserializeFontFamilies(fonts)
        };
    }

    completeShowcaseCreativeSetTranslation(
        creativesetId: string,
        showcaseKey: string
    ): Observable<IShowcaseCreativesetUpdateResponse> {
        return this.apollo
            .mutate<{ updateCreativesetShowcase: IShowcaseCreativesetUpdateResponse }>({
                mutation: COMPLETE_CREATIVESET_SHOWCASE_TRANSLATION,
                variables: {
                    creativesetShowcase: {
                        disabled: true,
                        notifyCreator: true,
                        showcaseKey: showcaseKey,
                        creativesetId: creativesetId
                    }
                }
            })
            .pipe(map(({ data }) => data!.updateCreativesetShowcase));
    }
}
